// Authentication utility functions

/**
 * Get current user from localStorage
 * @returns {Object|null} User object with id, or null if not found
 */
export const getCurrentUser = () => {
    try {
        // Try to get user data from localStorage
        const userData = localStorage.getItem('user');
        if (userData) {
            const user = JSON.parse(userData);
            console.log('getCurrentUser - Raw user data from localStorage:', user); // Debug log
            const currentUser = {
                id: user.id || user.user_id,
                name: user.name,
                email: user.email,
                status: user.status,
                packageType: user.package_type || user.packageType,
                accountId: user.account_id || user.accountId,
                accountType: user.account_type || user.accountType,
                webinarStatus: user.webinar_status || user.webinarStatus
            };
            console.log(`getCurrentUser - Processed user at ${new Date().toISOString()}:`, currentUser); // Debug log
            return currentUser;
        }

        // Fallback: check for auth token
        const token = localStorage.getItem('authToken');
        if (token) {
            // In a real app, you might decode JWT token here
            console.warn('User data not found in localStorage, but auth token exists');
            return null;
        }

        return null;
    } catch (error) {
        console.error('Error getting current user:', error);
        return null;
    }
};

/**
 * Get current user ID
 * @returns {number|null} User ID or null if not found
 */
export const getCurrentUserId = () => {
    const user = getCurrentUser();
    return user ? user.id : null;
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if user is logged in
 */
export const isAuthenticated = () => {
    const user = getCurrentUser();
    const token = localStorage.getItem('authToken');
    return !!(user && user.id && token);
};

/**
 * Set user data in localStorage
 * @param {Object} userData - User data to store
 */
export const setCurrentUser = (userData) => {
    try {
        localStorage.setItem('user', JSON.stringify(userData));
        console.log('setCurrentUser - Stored user data:', userData);
    } catch (error) {
        console.error('Error setting user data:', error);
    }
};

/**
 * Update user data in localStorage with new fields
 * @param {Object} updates - Fields to update
 */
export const updateCurrentUser = (updates) => {
    try {
        const currentUser = getCurrentUser();
        if (currentUser) {
            const updatedUser = { ...currentUser, ...updates };
            setCurrentUser(updatedUser);
            console.log('updateCurrentUser - Updated user data:', updatedUser);
            return updatedUser;
        }
        return null;
    } catch (error) {
        console.error('Error updating user data:', error);
        return null;
    }
};

/**
 * Clear user data from localStorage
 */
export const clearCurrentUser = () => {
    try {
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
    } catch (error) {
        console.error('Error clearing user data:', error);
    }
};

/**
 * Require authentication - redirect to login if not authenticated
 * @param {Function} navigate - React Router navigate function
 */
export const requireAuth = (navigate) => {
    if (!isAuthenticated()) {
        navigate('/login');
        return false;
    }
    return true;
};
